import { ErrorPage } from '@common/ErrorPage'
import { LoadingPage } from '@common/LoadingPage'
import { DetailView } from '@components/enitity'
import { withDetailView } from '@components/enitity/detail/Detail.view'
import { SectionHeading } from '@components/section'
import { ChatWidget } from '@features/chat/views/ChatWidget'
import { OffersTable } from '@features/offers'
import { EditPartnerModal } from '@features/referral/components/EditPartnerModal'
import { PromotePartnerModal } from '@features/referral/components/PromotePartnerModal'
import { UserNotesSection } from '@features/users/components/UserNotesSection'
import { UsersForm } from '@features/users/components/UsersForm'
import { WasContactedToggle } from '@features/users/components/WasContactedToggle'
import { useDemotePartner } from '@services/referral'
import { usePatchClient } from '@services/users/mutations/usePatchClient'
import { useClientDetail } from '@services/users/queries/useClientDetail'
import {
  ClipboardList,
  ExternalLink,
  Handshake,
  Tag as TagIcon
} from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import { UserDetail } from 'types/api'

import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { cn } from '@/lib/utils'

function IndexBar({ percent, label }: { percent?: number; label: string }) {
  const value = Math.max(0, Math.min(100, Math.floor(percent ?? 0)))
  return (
    <div className="min-w-0 flex-1">
      <div className="flex items-baseline justify-between gap-3">
        <span className="text-sm font-medium text-ink/70">{label}</span>
        <span className="font-display text-lg font-bold leading-none text-primary">
          {value}%
        </span>
      </div>
      <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-ink/[0.06]">
        <div
          className="h-full rounded-full bg-primary transition-all duration-500"
          style={{ width: `${value}%` }}
        />
      </div>
    </div>
  )
}

export function UsersDetailContent() {
  const { id } = useParams()
  const { t } = useTranslation('users')
  const navigate = useNavigate()
  const [isPromoteModalOpen, setIsPromoteModalOpen] = useState(false)
  const [isEditModalOpen, setIsEditModalOpen] = useState(false)
  const demotePartnerMutation = useDemotePartner()
  const { data, isLoading, refetch } = useClientDetail(id)
  const { mutate: patchFromData } = usePatchClient()
  const isPartner = data?.is_partner ?? false

  if (!id) {
    return <ErrorPage />
  }

  const handleDemotePartner = async () => {
    if (!data?.id) return
    if (
      window.confirm(
        `Are you sure you want to demote ${data.first_name} ${data.last_name} from partner to user? This will disable all their referral codes.`
      )
    ) {
      try {
        await demotePartnerMutation.mutateAsync({ id: data.id })
        refetch()
      } catch (error) {
        console.error('Failed to demote partner:', error)
      }
    }
  }

  function submitForm(values: UserDetail) {
    // birth_date already arrives as an ISO `YYYY-MM-DD` string from DatePicker.
    if (values.phone) {
      values.phone = values.phone.replace(/\s/g, '')
    }
    const o = Object.fromEntries(
      Object.entries(values).filter(([_, v]) => v != null)
    )
    // A cleared degree must be sent explicitly as null — the filter above drops
    // it otherwise and the backend keeps the old value.
    patchFromData({ id, body: { ...o, degree: values.degree ?? null } })
  }

  if (isLoading) {
    return <LoadingPage />
  }

  if (!data?.id) {
    return <></>
  }

  const name =
    `${data.first_name ?? ''} ${data.last_name ?? ''}`.trim() ||
    data.email ||
    '—'

  return (
    <DetailView.Root>
      <DetailView.Breadcrumb
        parentLabel={t('users')}
        parentTo="/admin/users"
        current={name}
      />

      <DetailView.Content>
        <div className="space-y-8">
          <Card className="flex flex-col gap-6 p-6 lg:flex-row lg:items-center lg:justify-between lg:gap-10">
            <div className="flex flex-1 flex-col gap-5 sm:flex-row sm:gap-8">
              <IndexBar
                percent={data.completition_index?.profile_completition}
                label={t('completition_index.profile_index')}
              />
              <IndexBar
                percent={data.completition_index?.application_completition}
                label={t('completition_index.application_index')}
              />
            </div>
            <div className="flex flex-wrap items-center gap-3 lg:shrink-0">
              <WasContactedToggle
                userId={data.id}
                wasContacted={data.was_contacted ?? false}
              />
              <Button
                variant="ghostDark"
                onClick={() =>
                  navigate(`/admin/applications/${data.application}`)
                }
              >
                <ExternalLink className="h-4 w-4" />
                {t('form.buttons.openApplication')}
              </Button>
            </div>
          </Card>

          <Card className="space-y-4 p-6">
            <div className="flex items-center gap-2.5">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
                <Handshake className="h-5 w-5" />
              </span>
              <h2 className="m-0 font-display text-lg font-bold tracking-tight text-ink">
                {t('partnerSection.title')}
              </h2>
            </div>
            <p className="m-0 max-w-2xl text-sm leading-relaxed text-muted-foreground">
              {t('partnerSection.description')}
            </p>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <span
                className={cn(
                  'inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-sm font-semibold',
                  isPartner
                    ? 'bg-primary/15 text-primary'
                    : 'bg-ink/[0.07] text-ink/60'
                )}
              >
                {isPartner
                  ? t('partnerSection.isPartner')
                  : t('partnerSection.notPartner')}
              </span>
              <div className="flex flex-wrap gap-2">
                {isPartner ? (
                  <>
                    <Button
                      variant="ghostDark"
                      onClick={() => setIsEditModalOpen(true)}
                    >
                      {t('form.buttons.editPartner')}
                    </Button>
                    <Button
                      onClick={handleDemotePartner}
                      disabled={demotePartnerMutation.isPending}
                      className="bg-destructive text-white hover:bg-destructive-strong"
                    >
                      {t('form.buttons.demotePartner')}
                    </Button>
                  </>
                ) : (
                  <Button onClick={() => setIsPromoteModalOpen(true)}>
                    <Handshake className="h-4 w-4" />
                    {t('form.buttons.makeAPartner')}
                  </Button>
                )}
              </div>
            </div>
          </Card>

          <section>
            <SectionHeading icon={ClipboardList}>
              {t('form.common.detail', { defaultValue: 'Detail' })}
            </SectionHeading>
            <UsersForm
              data={data}
              uploadPhoto={() => {}}
              submitForm={submitForm}
            />
          </section>

          <section>
            <SectionHeading icon={TagIcon}>{t('offers')}</SectionHeading>
            <OffersTable user={data.id} application={data.application} />
          </section>

          <section>
            <UserNotesSection userId={data.id} />
          </section>
        </div>
      </DetailView.Content>

      <ChatWidget user_id={data.id} />
      <PromotePartnerModal
        open={isPromoteModalOpen}
        onClose={() => setIsPromoteModalOpen(false)}
        onSuccess={() => setIsPromoteModalOpen(false)}
        userId={data.id}
      />
      <EditPartnerModal
        open={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        onSuccess={() => setIsEditModalOpen(false)}
        partnerId={data.id}
      />
    </DetailView.Root>
  )
}

export const UsersDetail = withDetailView(UsersDetailContent, 'User Detail')
